"use client";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { bdlcItems as steps } from "./bdlcData";
import Button from "./Button";
import { ItemList } from "./mobileBDLCScroll";
import { AuroraTextEffect } from "./TextAnimations/aurora-text";

export default function BDLCSection() {

  const [activeIndex, setActiveIndex] = useState(0);
  const [wrapperHeight, setWrapperHeight] = useState(800);

  const sectionRef = useRef<HTMLDivElement>(null);

  // ✅ Responsive wrapper height
  useEffect(() => {
    const updateHeight = () => {
      if (window.innerWidth < 1800) {
        setWrapperHeight(600);
      } else {
        setWrapperHeight(800);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // ✅ Scroll-based active index
  // ✅ Scroll-based active index
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate progress even if section is partially visible
      const scrollProgress = Math.min(
        1,
        Math.max(0, (windowHeight - rect.top) / (rect.height + windowHeight))
      );

      const newIndex = Math.min(
        steps.length - 1,
        Math.floor(scrollProgress * steps.length)
      );

      setActiveIndex(newIndex);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length]);


  // Vertical timeline math
  const VISIBLE_COUNT = 3;
  const ITEM_HEIGHT_PX = wrapperHeight / VISIBLE_COUNT;
  const CENTER_INDEX = Math.floor(VISIBLE_COUNT / 2);
  const PADDING_ITEMS = CENTER_INDEX;
  const totalHeightPx = (steps.length + PADDING_ITEMS * 2) * ITEM_HEIGHT_PX;
  const unclampedTranslateY =
    (activeIndex + PADDING_ITEMS) * ITEM_HEIGHT_PX - CENTER_INDEX * ITEM_HEIGHT_PX;
  const maxTranslateY = Math.max(0, totalHeightPx - wrapperHeight);
  const translateY = Math.min(Math.max(unclampedTranslateY, 0), maxTranslateY);
  const yWithinWrapper =
    (activeIndex + PADDING_ITEMS) * ITEM_HEIGHT_PX - translateY + ITEM_HEIGHT_PX / 2;
  const progressPercent = Math.min(
    100,
    Math.max(0, (yWithinWrapper / wrapperHeight) * 100)
  );

  return (
    <>
      {/* Desktop scroll-controlled pinned section */}
      <section
        ref={sectionRef}
        className="relative hidden lg:block"
        style={{ height: `${steps.length * 100}vh` }} // gives scroll duration
      >
        <div className="sticky top-0 h-screen overflow-hidden">
          <div className="custom-container h-full mx-auto px-4 md:px-8 flex items-center">
            {/* Title Section */}
            {/* <div className="absolute top-0 2xl:-right-[50.9%] -right-[47%] z-10 skew-custom-gradient backdrop-blur-xl w-full -skew-x-27 h-full"></div> */}

            <div className="w-1/2 z-50">
              <span className="text-white text-lg py-1 px-2 rounded-lg bg-[#d53333]">
                MMB
              </span>
              <h2 className="flex items-end gap-2">
                <Link href="brand-development-lifecycle">
                  <AuroraTextEffect
                    text="BDLC"
                    fontSize="clamp(2.5rem, 8vw, 3.75rem)"
                  />
                </Link>
                {/* <small className="text-sm mb-[3.5%] font-bold text-[#1b1b1b] relative">Powered by <span className="text-[#D53333]">AI</span>
                  <Image className="absolute -right-3.5 -top-2.5" src="/ai.svg" alt="AI" width={14} height={14} />
                </small> */}
              </h2>
              <p className="!text-xl text-[#1b1b1b] max-w-xs">
                Proprietary Framework for Consistent Brand
                Growth.
              </p>
              <h3 className="3xl:text-8xl 2xl:text-7xl text-5xl font-medium tracking-tight text-[#cccccc] mt-4 mb-12">
                brand development lifecycle
              </h3>
              <Link href="/brand-development-lifecycle">
                <Button size="lg">Get Custom Brand Growth Blueprint</Button>
              </Link>
            </div>

            {/* Animation Section */}
            <div className="w-2/3 h-full relative z-50  -right-[3.6%] 2xl:-right-[10%]">
              {/* Vertical timeline */}
              <div className="overflow-visible transform  -skew-x-[27deg]">
                <div
                  className="flex flex-col w-full relative transform h-screen -skew-x-0 overflow-x-visible"
                // style={{ height: `${wrapperHeight}px` }}
                >
                  <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-gray-300"></div>
                  <div
                    className="absolute left-[11px] top-0 w-0.5 bg-red-600 transition-all duration-300"
                    style={{ height: `${progressPercent}%` }}
                  ></div>

                  <div
                    className="relative flex flex-col transition-transform duration-500"
                    style={{ transform: `translateY(-${translateY}px)` }}
                  >
                    {Array.from({ length: PADDING_ITEMS }).map((_, idx) => (
                      <div
                        key={`top-spacer-${idx}`}
                        style={{ height: `${ITEM_HEIGHT_PX}px` }}
                      />
                    ))}

                    {steps.map((step, i) => {
                      const words = step.title.split(" ");
                      const splitIndex = Math.ceil(words.length / 2);
                      const firstLine = words.slice(0, splitIndex).join(" ");
                      const secondLine = words.slice(splitIndex).join(" ");
                      const isActive = i === activeIndex;
                      const isUpcoming = i > activeIndex;

                      return (
                        <div
                          key={i}
                          className="flex items-center relative cursor-pointer transition-all duration-300"
                          style={{ height: `${ITEM_HEIGHT_PX}px`, gap: "48px" }}
                        >
                          <div
                            className={`relative flex items-center justify-center transition-transform duration-300 transform skew-x-27 ${isActive ? "scale-[4] p-4" : "scale-100"
                              }`}
                          >
                            <div className="flex items-center justify-center 2xl:h-9 2xl:w-9 size-6 rounded-full bg-white shadow-[0_4px_74px_0_rgba(48,93,207,0.35)]">
                              {isActive ? (
                                <Image
                                  src={step.iconSrc}
                                  alt={`${step.title} icon`}
                                  width={24}
                                  height={24}
                                  className="object-contain w-4 h-4 2xl:w-6 2xl:h-6"
                                />
                              ) : (
                                <div
                                  className={`h-2 w-2 rounded-full ${isUpcoming ? "bg-gray-400" : "bg-red-600"
                                    }`}
                                ></div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col items-start text-left">
                            <div
                              className={`font-bold leading-tight transition-all duration-300 transform skew-x-27 origin-left ${isActive
                                ? "text-[#1b1b1b] 2xl:scale-125"
                                : isUpcoming
                                  ? "text-[#5060A1] opacity-40 blur-[1px]"
                                  : "text-[#C5C5C5]"
                                }`}
                              style={{
                                fontSize: isActive ? "1.6rem" : "0.875rem",
                              }}
                            >
                              {firstLine}
                              {secondLine && (
                                <>
                                  <br />
                                  {secondLine}
                                </>
                              )}
                            </div>
                            {step.description && (
                              <div
                                className={`mt-2 transition-all duration-300 w-[400px] transform skew-x-27 origin-left ${isActive
                                  ? "text-[#1b1b1b] 2xl:text-lg ml-8"
                                  : isUpcoming
                                    ? "text-[#5060A1]/1 text-sm ml-8 opacity-20 blur-[1px]"
                                    : "text-[#C5C5C5] text-sm ml-5"
                                  }`}
                              >
                                {step.description}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {Array.from({ length: PADDING_ITEMS }).map((_, idx) => (
                      <div
                        key={`bottom-spacer-${idx}`}
                        style={{ height: `${ITEM_HEIGHT_PX}px` }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Horizontal timeline */}
              <div className="absolute bottom-8 -left-[6%] 2xl:-left-[9%] 3xl:!left-[4%] max-w-3xl w-full bg-white/10 backdrop-blur-sm">
                <div className="relative py-10">
                  <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-0.5 bg-gray-300"></div>
                  <div
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-red-600 transition-all duration-300"
                    style={{
                      width: `${(activeIndex / (steps.length - 1)) * 100}%`,
                    }}
                  ></div>

                  <div className="relative flex justify-between items-center">
                    {steps.map((step, i) => {
                      const words = step.title.split(" ");
                      const splitIndex = Math.ceil(words.length / 2);
                      const firstLine = words.slice(0, splitIndex).join(" ");
                      const secondLine = words.slice(splitIndex).join(" ");
                      const isActive = i === activeIndex;
                      const isUpcoming = i > activeIndex;

                      return (
                        <div
                          key={i}
                          className="relative flex flex-col text-sm items-center text-center"
                        >
                          <div className="relative z-10 flex items-center justify-center">
                            <div className="h-6 w-6 rounded-full bg-white shadow-lg flex items-center justify-center">
                              <div
                                className={`h-2 w-2 rounded-full ${isUpcoming ? "bg-gray-400" : "bg-red-600"
                                  }`}
                              ></div>
                            </div>
                          </div>
                          <div
                            className={`absolute w-40 -translate-x-1/2 ${i % 2 === 0 ? "-top-12" : "top-6"
                              } leading-tight`}
                            style={{ left: "50%" }}
                          >
                            <span
                              className={`block font-semibold ${isActive
                                ? "text-[#1b1b1b]"
                                : isUpcoming
                                  ? "text-[#5060A1]"
                                  : "text-[#000]/48"
                                }`}
                            >
                              {firstLine}
                              {secondLine && (
                                <>
                                  <br />
                                  {secondLine}
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile version stays the same */}
      <div className="lg:hidden mt-10 px-4">
        <div>
          <span className="text-white text-lg py-1 px-2 rounded-lg bg-[#d53333]"> MMB </span>
          <h2 className="2xl:text-6xl md:text-4xl text-3xl font-bold tracking-tight mt-2 text-[#272d57]">BDLC</h2>
          <p className="text-base text-[#1b1b1b]">
            Strategic/Proprietary/Proven Framework for Consistent Brand Growth.
          </p>
          <h3 className="text-5xl font-medium tracking-tight text-[#DEDEDE4D] mt-4 mb-6">
            brand development lifecycle
          </h3>
          <Link href="/brand-development-lifecycle">
            <Button
              responsiveSize={{
                mobile: "md",
                tablet: "lg",
              }}
            >
              Get Custom Brand Growth Blueprint
            </Button>
          </Link>
        </div>
        <div className="py-5">
          <ItemList />
        </div>
      </div>
    </>
  );
}
